/**
 * ApiPlayground — embedded interactive API tester driven by the OpenAPI 3.0 spec.
 *
 * Auth : auto-injects the logged-in user's JWT Bearer token (no manual key entry).
 * Spec : fetches /api/openapi.json once (module-level cache) so any new backend
 *        endpoint automatically appears without frontend changes.
 * Body : handles both scalar bodies (single integer/string) and multi-field objects,
 *        resolving $ref component schemas automatically.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Play, ChevronDown, ChevronRight, Copy, Check,
  Loader2, AlertCircle, Clock, FlaskConical,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { tokenStore, apiKeysApi } from "@/lib/api";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BASE_URL}/api`;

/** Attempt a silent token refresh. Returns new token or null. */
async function tryRefreshToken() {
  const rt = tokenStore.getRefresh();
  if (!rt) return null;
  try {
    const res = await fetch(`${API_BASE}/v1/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data.access_token) {
      tokenStore.setAccess(data.access_token);
      if (data.refresh_token) tokenStore.setRefresh(data.refresh_token);
      return data.access_token;
    }
  } catch {}
  return null;
}

/** Execute a fetch and safely read the response body even if a proxy consumed it. */
async function safeFetch(url, opts) {
  const res = await fetch(url, opts);
  let text = "";
  try {
    text = await res.text();
  } catch {
    // Proxy (e.g. Cloudflare) consumed the body — return status with empty body
    text = "";
  }
  return { res, text };
}

// ── Spec cache (module-level — shared, fetched once) ─────────────────────────
let _spec = null;
let _specPending = null;

async function loadSpec() {
  if (_spec) return _spec;
  if (_specPending) return _specPending;
  _specPending = fetch(`${BASE_URL}/api/openapi.json`)
    .then((r) => r.json())
    .then((s) => { _spec = s; return s; })
    .catch(() => null);
  return _specPending;
}

// ── Schema helpers ────────────────────────────────────────────────────────────

function resolveRef(spec, ref) {
  // ref looks like "#/components/schemas/Body_vrf_generate_..."
  const parts = ref.replace("#/", "").split("/");
  return parts.reduce((acc, p) => acc?.[p], spec) ?? null;
}

function getBodySchema(spec, method, path) {
  const op = spec?.paths?.[path]?.[method.toLowerCase()];
  if (!op) return null;
  const raw = op.requestBody?.content?.["application/json"]?.schema;
  if (!raw) return null;
  if (raw.$ref) return resolveRef(spec, raw.$ref);
  return raw;
}

function getQueryParams(spec, method, path) {
  const op = spec?.paths?.[path]?.[method.toLowerCase()];
  if (!op) return [];
  return (op.parameters || []).filter((p) => p.in === "query");
}

// Build the initial request body value from the schema
function buildDefault(schema) {
  if (!schema) return "";
  // Scalar type (single Body param — sent as bare JSON value)
  if (schema.type && schema.type !== "object") {
    const def = schema.default;
    if (schema.type === "integer") return String(def ?? 32);
    if (schema.type === "number")  return String(def ?? 1.0);
    if (schema.type === "boolean") return String(def ?? false);
    return def ?? "";
  }
  // Object type
  const obj = {};
  Object.entries(schema.properties || {}).forEach(([k, v]) => {
    if (v.default !== undefined) obj[k] = v.default;
    else if (v.type === "integer") obj[k] = 1;
    else if (v.type === "number")  obj[k] = 1.0;
    else if (v.type === "boolean") obj[k] = false;
    else obj[k] = "";
  });
  return JSON.stringify(obj, null, 2);
}

function isScalarSchema(schema) {
  return schema?.type && schema.type !== "object" && schema.type !== "array";
}

// ── StatusBadge ───────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cls =
    status >= 200 && status < 300
      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
      : status >= 400 && status < 500
      ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
      : "bg-red-500/15 text-red-400 border-red-500/30";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded border font-mono text-xs font-semibold ${cls}`}>
      {status}
    </span>
  );
}

// ── ResponsePanel ─────────────────────────────────────────────────────────────
function ResponsePanel({ result }) {
  const [copied, setCopied] = useState(false);
  const [showHeaders, setShowHeaders] = useState(false);

  if (result.networkError) {
    return (
      <div className="flex items-start gap-2 p-3 bg-red-950/40 border border-red-500/20 rounded text-xs text-red-400">
        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        {result.networkError}
      </div>
    );
  }

  const bodyText =
    typeof result.body === "string"
      ? result.body
      : JSON.stringify(result.body, null, 2);

  const copy = () => {
    navigator.clipboard.writeText(bodyText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      // Clipboard permission denied — silently ignore
    });
  };

  return (
    <div className="space-y-3" data-testid="playground-response">
      {/* Status row */}
      <div className="flex items-center gap-3 text-xs">
        <StatusBadge status={result.status} />
        <span className="text-slate-500 font-mono">{result.statusText}</span>
        <span className="flex items-center gap-1 text-slate-500 ml-auto font-mono">
          <Clock className="w-3 h-3" /> {result.time}ms
        </span>
      </div>

      {/* Headers toggle */}
      <button
        onClick={() => setShowHeaders((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
        data-testid="toggle-response-headers"
      >
        {showHeaders ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Response Headers
      </button>
      {showHeaders && (
        <div className="bg-slate-900 border border-slate-800 p-3 font-mono text-[11px] space-y-0.5 max-h-36 overflow-auto">
          {Object.entries(result.headers || {}).map(([k, v]) => (
            <div key={k}>
              <span className="text-cyan-400">{k}:</span>{" "}
              <span className="text-slate-300">{v}</span>
            </div>
          ))}
        </div>
      )}

      {/* Body */}
      <div className="relative bg-slate-950 rounded overflow-hidden border border-slate-800">
        <button
          onClick={copy}
          className="absolute top-2.5 right-2.5 p-1 text-slate-500 hover:text-slate-300 transition-colors"
          data-testid="copy-live-response"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
        <pre className="p-4 text-xs font-mono text-slate-300 leading-relaxed overflow-auto max-h-80 whitespace-pre">
          {bodyText}
        </pre>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ApiPlayground({ endpoint }) {
  const [open, setOpen]           = useState(false);
  const [spec, setSpec]           = useState(null);
  const [bodySchema, setBodySchema] = useState(null);   // resolved JSON schema
  const [queryDefs, setQueryDefs] = useState([]);       // query param definitions
  const [body, setBody]           = useState("");       // textarea / input value
  const [queryVals, setQueryVals] = useState({});       // GET query param values
  const [bodyErr, setBodyErr]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [result, setResult]       = useState(null);
  const resultRef                 = useRef(null);

  const specPath  = `/api${endpoint.path}`;            // doc /v1/... → spec /api/v1/...
  const isPost    = endpoint.method === "POST";
  const isScalar  = isScalarSchema(bodySchema);

  // ── Load spec once ──────────────────────────────────────────────────────────
  useEffect(() => {
    loadSpec().then(setSpec);
  }, []);

  // ── Re-derive schema when endpoint or spec changes ─────────────────────────
  useEffect(() => {
    setResult(null);
    setBodyErr(null);

    if (!spec) return;

    if (isPost) {
      const schema = getBodySchema(spec, "POST", specPath);
      setBodySchema(schema);
      setBody(buildDefault(schema));
    } else {
      const qp = getQueryParams(spec, endpoint.method, specPath);
      setQueryDefs(qp);
      const defaults = {};
      qp.forEach((p) => { defaults[p.name] = p.schema?.default ?? ""; });
      setQueryVals(defaults);
    }
  }, [endpoint, spec, isPost, specPath]);

  // ── Execute ─────────────────────────────────────────────────────────────────
  const execute = useCallback(async () => {
    setLoading(true);
    setResult(null);
    setBodyErr(null);

    const token = tokenStore.getAccess();
    if (!token) {
      setResult({ networkError: "No active session — please log in again." });
      setLoading(false);
      return;
    }

    // Build URL
    let url = `${BASE_URL}/api${endpoint.path}`;
    if (!isPost && Object.keys(queryVals).length) {
      const qs = Object.entries(queryVals)
        .filter(([, v]) => v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&");
      if (qs) url += `?${qs}`;
    }

    // Build body
    let rawBody;
    if (isPost && bodySchema) {
      if (isScalar) {
        // Send bare JSON value (e.g. just 32 for count)
        rawBody = body.trim();
      } else {
        try {
          rawBody = JSON.stringify(JSON.parse(body));
        } catch {
          setBodyErr("Invalid JSON — please fix the request body.");
          setLoading(false);
          return;
        }
      }
    }

    const start = Date.now();
    try {
      let activeToken = token;
      const buildOpts = (t) => ({
        method: endpoint.method,
        headers: {
          Authorization: `Bearer ${t}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: rawBody,
      });

      let { res, text } = await safeFetch(url, buildOpts(activeToken));

      // On 401, silently refresh and retry once
      if (res.status === 401) {
        const newToken = await tryRefreshToken();
        if (newToken) {
          activeToken = newToken;
          ({ res, text } = await safeFetch(url, buildOpts(activeToken)));
        }
      }

      const time = Date.now() - start;
      let responseBody;
      if (text) {
        try { responseBody = JSON.parse(text); } catch { responseBody = text; }
      } else {
        responseBody = `${res.status} ${res.statusText} — no response body returned by server`;
      }

      const headers = {};
      res.headers.forEach((v, k) => { headers[k] = v; });

      setResult({ status: res.status, statusText: res.statusText, headers, body: responseBody, time });
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 80);
    } catch (err) {
      setResult({ networkError: `Network error: ${err.message}` });
    } finally {
      setLoading(false);
    }
  }, [endpoint, isPost, isScalar, bodySchema, body, queryVals]);

  // ── Check if endpoint exists in spec ────────────────────────────────────────
  const specExists = spec
    ? !!spec.paths?.[specPath]?.[endpoint.method.toLowerCase()]
    : null; // null = loading

  return (
    <div className="border border-slate-200 bg-white" data-testid="api-playground">
      {/* Header — always visible */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-3.5 border-b border-slate-200 hover:bg-slate-50 transition-colors"
        data-testid="playground-toggle"
      >
        <div className="flex items-center gap-2.5">
          <FlaskConical className="w-3.5 h-3.5 text-cyan-600" />
          <span className="text-sm font-semibold text-slate-900">Try it out</span>
          <span className="text-xs text-slate-400">— live interactive tester</span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="p-6 space-y-5">

          {/* Endpoint not yet in live spec */}
          {specExists === false && (
            <div className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 text-sm text-slate-500">
              <FlaskConical className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
              <div>
                <p className="font-medium text-slate-700 mb-0.5">Endpoint in development</p>
                <p className="text-xs">
                  <code className="font-mono">{endpoint.method} {endpoint.path}</code> is documented in the API spec
                  but not yet deployed to this environment. Check back soon or see the code examples below.
                </p>
              </div>
            </div>
          )}

          {/* Spec still loading */}
          {specExists === null && (
            <div className="flex items-center gap-2 text-xs text-slate-400 py-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading spec…
            </div>
          )}

          {/* Auth notice */}
          {specExists && (
            <>
              <div className="flex items-center gap-2 px-3 py-2 bg-cyan-50 border border-cyan-100 text-xs text-cyan-700 rounded-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0" />
                Authenticated with your active session — requests are tenant-scoped and go through live rate-limiting
              </div>

              {/* POST: scalar input */}
              {isPost && isScalar && bodySchema && (
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-slate-500 font-medium mb-2">
                    {bodySchema.title || "count"}{" "}
                    <span className="normal-case tracking-normal text-slate-400 font-normal">
                      ({bodySchema.type}
                      {bodySchema.minimum !== undefined ? `, min ${bodySchema.minimum}` : ""}
                      {bodySchema.maximum !== undefined ? `, max ${bodySchema.maximum}` : ""})
                    </span>
                  </label>
                  <input
                    type="number"
                    value={body}
                    min={bodySchema.minimum}
                    max={bodySchema.maximum}
                    onChange={(e) => setBody(e.target.value)}
                    className="w-full max-w-xs bg-slate-50 border border-slate-200 text-sm font-mono px-3 py-2 focus:outline-none focus:border-cyan-500 transition-colors text-slate-900"
                    data-testid="playground-scalar-input"
                  />
                </div>
              )}

              {/* POST: object JSON editor */}
              {isPost && !isScalar && bodySchema && (
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-slate-500 font-medium mb-2">
                    Request Body <span className="text-slate-400 normal-case tracking-normal">(JSON)</span>
                  </label>
                  <div className="relative">
                    <textarea
                      value={body}
                      onChange={(e) => { setBody(e.target.value); setBodyErr(null); }}
                      rows={Math.max(5, (body.match(/\n/g) || []).length + 2)}
                      className={`w-full bg-slate-950 text-slate-200 font-mono text-xs p-4 focus:outline-none resize-none leading-relaxed border ${bodyErr ? "border-red-500/50" : "border-slate-800 focus:border-cyan-600/60"} transition-colors`}
                      spellCheck={false}
                      data-testid="playground-body-editor"
                    />
                    {bodyErr && (
                      <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />{bodyErr}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* POST: no body expected */}
              {isPost && !bodySchema && spec && (
                <p className="text-xs text-slate-400 italic">No request body required for this endpoint.</p>
              )}

              {/* GET: query param inputs */}
              {!isPost && queryDefs.length > 0 && (
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-slate-500 font-medium mb-2">
                    Query Parameters
                  </label>
                  <div className="border border-slate-100 divide-y divide-slate-100">
                    {queryDefs.map((p) => (
                      <div key={p.name} className="flex items-center gap-4 px-3 py-2.5">
                        <code className="text-xs font-mono text-cyan-700 bg-cyan-50 px-1.5 py-0.5 w-28 shrink-0 text-center">
                          {p.name}
                        </code>
                        <input
                          type={p.schema?.type === "integer" ? "number" : "text"}
                          placeholder={`${p.schema?.type || "string"}${p.required ? " · required" : " · optional"}`}
                          value={queryVals[p.name] ?? ""}
                          onChange={(e) => setQueryVals((prev) => ({ ...prev, [p.name]: e.target.value }))}
                          className="flex-1 bg-slate-50 border border-slate-200 text-xs font-mono px-2.5 py-1.5 focus:outline-none focus:border-cyan-500 transition-colors text-slate-900"
                          data-testid={`param-input-${p.name}`}
                        />
                        {p.description && (
                          <span className="text-xs text-slate-400 w-48 truncate hidden lg:block">{p.description}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* GET: no params */}
              {!isPost && queryDefs.length === 0 && spec && (
                <p className="text-xs text-slate-400 italic">No query parameters — just hit Execute.</p>
              )}

              {/* Execute button */}
              <Button
                onClick={execute}
                disabled={loading}
                className="bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-mono px-5 h-9 gap-2 rounded-none"
                data-testid="playground-execute-btn"
              >
                {loading ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Executing…</>
                ) : (
                  <><Play className="w-3 h-3" /> Execute</>
                )}
              </Button>

              {/* Response */}
              {result && (
                <div ref={resultRef}>
                  <div className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium mb-3">
                    Live Response
                  </div>
                  <ResponsePanel result={result} />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
